import { TargetTypeSchema } from '@terra/shared-schemas-orchestrator';
import { MongoObjectIdSchema } from '@terra/shared-utils';
import { z } from 'zod';
import {
  ONBOARDING_STEP_KEY_VALUES,
  ONBOARDING_STEP_STATUS_VALUES,
  ONBOARDING_STEP_TRANSITION_VALUES,
} from './onboarding-timeline.constants';

export const OnboardingStepKeySchema = z.enum(ONBOARDING_STEP_KEY_VALUES);

export type OnboardingStepKey = z.infer<typeof OnboardingStepKeySchema>;

export const OnboardingStepStatusSchema = z.enum(ONBOARDING_STEP_STATUS_VALUES);

export type OnboardingStepStatus = z.infer<typeof OnboardingStepStatusSchema>;

export const OnboardingTimelineIssueSchema = z.object({
  code: z.string(),
  message: z.string(),
  ctaType: z.string().optional(),
});

export type OnboardingTimelineIssue = z.infer<
  typeof OnboardingTimelineIssueSchema
>;

export const OnboardingTimelineSubStepSchema = z.object({
  key: z.string(),
  status: OnboardingStepStatusSchema,
  startedAt: z.string().datetime().nullable(),
  completedAt: z.string().datetime().nullable(),
  failedAt: z.string().datetime().nullable(),
  skippedReason: z.string().nullable(),
  issue: OnboardingTimelineIssueSchema.nullable(),
  attemptId: z.number().int().nonnegative().optional(),
});

export type OnboardingTimelineSubStep = z.infer<
  typeof OnboardingTimelineSubStepSchema
>;

export const OnboardingTimelineStepSchema = z.object({
  key: OnboardingStepKeySchema,
  status: OnboardingStepStatusSchema,
  startedAt: z.string().datetime().nullable(),
  completedAt: z.string().datetime().nullable(),
  failedAt: z.string().datetime().nullable(),
  skippedReason: z.string().nullable(),
  failureReason: z.string().nullable(),
  subSteps: z.array(OnboardingTimelineSubStepSchema),
});

export type OnboardingTimelineStep = z.infer<
  typeof OnboardingTimelineStepSchema
>;

export const InitialSetupSnapshotSchema = z.object({
  credentialIds: z.array(z.string()),
  repositoryNames: z.array(z.string()),
  apiDocumentationFileIds: z.array(z.string()),
  scopingDocumentationFileIds: z.array(z.string()),
  hasUrl: z.boolean(),
  url: z.string().optional(),
  dynamicApplicationScanEnabled: z.boolean().default(false),
});

export type InitialSetupSnapshot = z.infer<typeof InitialSetupSnapshotSchema>;

export const TargetOnboardingTimelineSchema = z.object({
  id: z.string(),
  targetType: TargetTypeSchema,
  targetId: z.string(),
  startedAt: z.string().datetime(),
  completedAt: z.string().datetime().nullable(),
  probePhaseStartedAt: z.string().datetime().nullable(),
  lastEndpointDiscoveredAt: z.string().datetime().nullable(),
  initialSetupSnapshot: InitialSetupSnapshotSchema,
  steps: z.array(OnboardingTimelineStepSchema),
});

export type TargetOnboardingTimelineDto = z.infer<
  typeof TargetOnboardingTimelineSchema
>;

export const GetOnboardingTimelineResponseSchema = z.object({
  timeline: TargetOnboardingTimelineSchema.nullable(),
});

export type GetOnboardingTimelineResponse = z.infer<
  typeof GetOnboardingTimelineResponseSchema
>;

export const APPLICATIONS_ONBOARDING_TIMELINES_MAX_APPLICATION_IDS = 200;

export const GetOnboardingTimelinesBodySchema = z.object({
  applicationIds: z
    .array(MongoObjectIdSchema)
    .max(APPLICATIONS_ONBOARDING_TIMELINES_MAX_APPLICATION_IDS),
});

export const GetOnboardingTimelinesItemSchema = z.object({
  applicationId: z.string().min(1),
  timeline: TargetOnboardingTimelineSchema.nullable(),
});

export const GetOnboardingTimelinesResponseSchema = z.object({
  items: z.array(GetOnboardingTimelinesItemSchema),
});

export type GetOnboardingTimelinesResponse = z.infer<
  typeof GetOnboardingTimelinesResponseSchema
>;

export const OnboardingStepTransitionSchema = z.enum(
  ONBOARDING_STEP_TRANSITION_VALUES,
);

export type OnboardingStepTransition = z.infer<
  typeof OnboardingStepTransitionSchema
>;

export const RecordOnboardingStepTransitionPayloadSchema = z.object({
  targetType: TargetTypeSchema,
  targetId: z.string(),
  stepKey: OnboardingStepKeySchema,
  subStepKey: z.string().optional(),
  transition: OnboardingStepTransitionSchema,
  reason: z.string().optional(),
  issue: OnboardingTimelineIssueSchema.optional(),
  entityId: z.string().optional(),
  isRetry: z.boolean().optional(),
  attemptId: z.number().int().nonnegative().optional(),
});

export type RecordOnboardingStepTransitionPayload = z.infer<
  typeof RecordOnboardingStepTransitionPayloadSchema
>;
